import { computedFrom } from "aurelia-binding";
import * as Enumerable from "linq";
import { MepYearOverYearEmissionResultItemModel, MepGetYearOverYearEmissionStatisticsRequest, MepGetYearOverYearEmissionsRequest, MepYearOverYearLegendItem } from "../../../../../../../../models/mep/mep-year-on-year.models";
import { EmissionTypeCategoryEnum, MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepYearOverYearService } from "../../../../../../../../services/mep/mep-year-on-year-service";
import { MepYearOnYearComparisonChartViewModel } from "./mep-year-on-year-comparison-chart-vm";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";

@useView('views/pages/modules/mep/tabs/mep-year-over-year-comparison/partials/mep-year-over-year-comparison-line-chart/mep-year-over-year-comparison-line-chart.html')
export class MepYearOverYearComparisonLineChart {
    portId: number;

    years: MepLookupItem[];
    emissionTypes: MepLookupItem[];
    pointsOfInterests: MepLookupItem[];
    operationModes: MepLookupItem[];
    vesselTypeGroups: MepLookupItem[];
    isGhgEmissions: boolean = true;

    //Emission Types Filter
    selectedEmissionTypes: MepLookupItem[] = [];
    //Point of Interest Filter
    selectedPointsOfInterest: MepLookupItem[] = [];
    //Operation Mode Filter
    selectedOperationModes: MepLookupItem[] = [];
    //Vessel Type Filter
    selectedVesselTypeGroups: MepLookupItem[] = [];

    ghgEmissionsChartViewModel: MepYearOnYearComparisonChartViewModel;
    pollutantsEmissionsChartViewModel: MepYearOnYearComparisonChartViewModel;
    ghgEmissionsItems: MepYearOverYearEmissionResultItemModel[];
    criteriaPollutantsItems: MepYearOverYearEmissionResultItemModel[];

    @computedFrom('isGhgEmissions', 'ghgEmissionsChartViewModel', 'pollutantsEmissionsChartViewModel')
    public get emissionsChartViewModel(): MepYearOnYearComparisonChartViewModel {
        return this.isGhgEmissions ? this.ghgEmissionsChartViewModel : this.pollutantsEmissionsChartViewModel;
    }

    @computedFrom('isGhgEmissions', 'ghgEmissionsChartViewModel', 'pollutantsEmissionsChartViewModel')
    public get emissionsChartViewModels(): MepYearOnYearComparisonChartViewModel[] {
        return [this.ghgEmissionsChartViewModel, this.pollutantsEmissionsChartViewModel];
    }

    ghgLineChartLegend: MepYearOverYearLegendItem[] = null;
    pollutantsLineChartLegend: MepYearOverYearLegendItem[] = null;
    lineChartResults: MepYearOverYearEmissionResultItemModel[] = [];
    loadingInitData: boolean = false;
    isRefreshingData: boolean = false;
    filtersAppliedAtLeastOnce: boolean = false;

    baseYear = 0;

    public setBaseYear(year) {
        this.baseYear = year;
    }

    getRequestFunc: () => MepGetYearOverYearEmissionStatisticsRequest;
    getYearColorFunc: (year: number) => string;

    constructor(
        private mepYearOnYearService: MepYearOverYearService,
        private notificationClientService: NotificationClientService,
        getRequestFunc: () => MepGetYearOverYearEmissionStatisticsRequest,
        getYearColorFunc: (year: number) => string, private configService: ConfigService) {
        this.getRequestFunc = getRequestFunc;
        this.getYearColorFunc = getYearColorFunc;
        this.resetAllFilters(false);
    }

    applyAllFilters() {
        if (this.isRefreshingData)
            return;

        this.filtersAppliedAtLeastOnce = true;
        this.isRefreshingData = true;
        this.loadData().then(() => {
            this.isRefreshingData = false;
        }).catch(e => {
            this.isRefreshingData = false;
            if (e) {
                this.notificationClientService.show("MEP Error", e, NotificationType.Error);
            }
        });

    }

    resetAllFilters(shouldRefresh: boolean) {
        this.selectedEmissionTypes = [];
        this.selectedOperationModes = [];
        this.selectedPointsOfInterest = [];
        this.selectedVesselTypeGroups = [];
        if (shouldRefresh) {
            this.applyAllFilters();
        }
    }

    init(portId: number) {
        this.portId = portId;
    }

    loadData(): Promise<string> {
        return new Promise<string>((resolve, reject) => {

            const baseRequest = this.getRequestFunc();

            if (!baseRequest) {
                reject(null); // reject with null since getRequestFunc is already doing notification client error message 
                return;
            }

            const request: MepGetYearOverYearEmissionsRequest = {
                baseYear: baseRequest.baseYear,
                comparisonYears: baseRequest.comparisonYears,
                portId: baseRequest.portId,
                emissionTypeIds: MepLookupItem.getIdsFromLookups(this.emissionTypes, this.selectedEmissionTypes),
                vesselTypeGroupIds: MepLookupItem.getIdsFromLookups(this.vesselTypeGroups, this.selectedVesselTypeGroups),
                pointOfInterestIds: MepLookupItem.getIdsFromLookups(this.pointsOfInterests, this.selectedPointsOfInterest),
                operationModeIds: MepLookupItem.getIdsFromLookups(this.operationModes, this.selectedOperationModes),
            };
            this.ghgEmissionsChartViewModel = null;
            this.pollutantsEmissionsChartViewModel = null;
            this.mepYearOnYearService.getYearOverYearEmissions(request)
                .then(result => {

                    if (result.hasError) {
                        reject("Error loading Year over Year Emissions");
                    } else {
                        this.ghgEmissionsChartViewModel = new MepYearOnYearComparisonChartViewModel("chartghgdiv", result.items.filter(x => x.emissionTypeGroup == EmissionTypeCategoryEnum.GhgEmissions), this.getYearColorFunc, this.configService);
                        this.pollutantsEmissionsChartViewModel = new MepYearOnYearComparisonChartViewModel("chartpollutantsdiv", result.items.filter(x => x.emissionTypeGroup != EmissionTypeCategoryEnum.GhgEmissions), this.getYearColorFunc, this.configService);
                        this.ghgEmissionsItems = result.items.filter(x => x.emissionTypeGroup == EmissionTypeCategoryEnum.GhgEmissions);
                        this.criteriaPollutantsItems = result.items.filter(x => x.emissionTypeGroup == EmissionTypeCategoryEnum.CriteriaPollutants);
                        this.isGhgEmissions = this.ghgEmissionsItems.length > 0 ? true : this.criteriaPollutantsItems.length > 0 ? false : false;

                        const items = Enumerable.from(result.items);
                        items.groupBy(x => x.emissionTypeGroup).forEach(group => {
                            const isGhgEmissions = group.key() == EmissionTypeCategoryEnum.GhgEmissions;
                            const baseYearTotal = group.where(x => x.emissionYear == baseRequest.baseYear).sum(x => x.emissionValueInTonnes);
                            const lineChartLegend = group.groupBy(x => x.emissionYear)
                                .select(x => {
                                    const totalForYear = x.sum(y => y.emissionValueInTonnes);
                                    const percentage = ((totalForYear - baseYearTotal) / (baseYearTotal == 0 ? 1 : baseYearTotal) * 100);
                                    return {
                                        year: `FY ${x.key().toString()}`,
                                        totalEmissionValueInTonnes: totalForYear,
                                        percentageChangeFromBaseYear: percentage.toFixed(2),
                                        absPercentageChangeFromBaseYear: Math.abs(percentage).toFixed(2),
                                        color: (isGhgEmissions ? this.ghgEmissionsChartViewModel.legendColors[x.key()] :
                                            this.pollutantsEmissionsChartViewModel.legendColors[x.key()]) ?? "#003745",
                                        isBaseYear: this.baseYear == x.key()
                                    };
                                }).toArray().sort((x) => { // this to ensure base year is first 
                                    return x && x.year == `FY ${this.baseYear}` ? -1 : 1;
                                });
                            if (isGhgEmissions) {
                                this.ghgLineChartLegend = lineChartLegend;
                            } else {
                                this.pollutantsLineChartLegend = lineChartLegend;
                            }
                        });
                        this.lineChartResults = result.items;
                        resolve(null);
                    }
                }).catch(() => {
                    reject("Error loading Year over Year Emissions");
                });
        });
    }

    @computedFrom("lineChartResults")
    get hasAnyChartData(): boolean {
        if (!this.filtersAppliedAtLeastOnce) return true; // we do an exception for when filters where never applied 
        return this.lineChartResults && this.lineChartResults.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<MepYearOverYearComparisonLineChart>(z => z.ghgEmissionsItems))
    public get isGHGEmissionsFilter() {
        return this.ghgEmissionsItems && this.ghgEmissionsItems.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<MepYearOverYearComparisonLineChart>(z => z.criteriaPollutantsItems))
    public get isCriteriaPollutantsFilter() {
        return this.criteriaPollutantsItems && this.criteriaPollutantsItems.length > 0;
    }

    filterChanged($event, array: any[]) {
        const notSelected = $event.detail.filter(x => !x.isSelected);

        notSelected.forEach(x => {
            const index = array.indexOf(x.id);
            if (index >= 0)
                array.splice(index, 1);
        });

        const newItems = $event.detail.filter(x => x.isSelected && array.indexOf(x.id) == -1);
        newItems.forEach(x => {
            array.push(x.id);
        });

        return array;
    }

    setIsGhgEmission = (value: boolean) => {
        this.isGhgEmissions = value;
    }
}
